import { useDispatch } from 'react-redux';
import { log } from 'log';
import { prop, pick } from 'ramda';
import { useFocus } from 'js/hooks/useFocus';
import { useTranslations } from 'js/hooks/i18n';
import { Icon } from 'js/components/common/Icon';
import { Link } from 'js/components/common/Link';
import { Button } from 'js/components/common/Button';
import { Balance } from 'js/components/main/Balance';
import React, { useCallback, useEffect, Suspense } from 'react';
import { showBalancesPopupAction } from '../../../../popups/BalancesPopup';
import { useUserSelector, useAppSelector, useMyAccountSidebarSelector } from 'js/store/hooks';

import * as hooks from 'js/components/main/MyAccountSidebar/hooks';
import * as actions from 'js/components/main/MyAccountSidebar/store/actions';

import style from './Header.scss';

export const Header = () => {
    const dispatch = useDispatch();
    const translate = useTranslations();
    const [focusRef, setFocus] = useFocus();
    const isLoggedIn = useUserSelector(prop('isLoggedIn'));
    const handleSidebarClose = hooks.useHandleSidebarClose();
    const {
        name,
        isOpened,
        portalPageURL,
        isBackArrowHidden,
        sidebarMenu: { isSubMenuShown }
    } = useMyAccountSidebarSelector(pick(['name', 'isOpened', 'portalPageURL', 'isBackArrowHidden', 'sidebarMenu']));
    const { DTAG_PAGES_WITH_BALANCE } = useAppSelector(prop('dynamicTags'));

    const handleBackArrowClick = useCallback(() => {
        portalPageURL ? dispatch(actions.showHomePage()) : dispatch(actions.showMainMenu());
    }, [dispatch, portalPageURL]);

    const handleOpenBalancePopup = useCallback(() => {
        dispatch(showBalancesPopupAction());
    }, [dispatch]);

    useEffect(() => {
        if (!DTAG_PAGES_WITH_BALANCE) {
            log('error', 'DTAG_PAGES_WITH_BALANCE DOES NOT SETUP');
        }
    }, [DTAG_PAGES_WITH_BALANCE]);

    useEffect(() => {
        if (isOpened) {
            setTimeout(() => {
                setFocus();
            }, 0);
        }
    }, [isOpened, setFocus]);

    const middle = name !== null ? (
        <p className={style.name}>{name}</p>
    ) : (
        <div className={style.logoContainer}>
            <Link href='/kambi#home' className={style.logo}></Link>
        </div>
    );

    const showBackButton = portalPageURL && !isBackArrowHidden || !portalPageURL && isSubMenuShown;
    const isBalanceButtonShown = isLoggedIn && DTAG_PAGES_WITH_BALANCE && DTAG_PAGES_WITH_BALANCE.includes(portalPageURL);

    return (
        <div className={style.header}>
            <span ref={focusRef} tabIndex={-1} aria-hidden={true} />
            {showBackButton && (
                <div className={style.backIconContainer}>
                    <Icon
                        role="button"
                        type={'back_arrow'}
                        className={style.backIcon}
                        onClick={handleBackArrowClick}
                        aria-label={translate('playtech.mobile.system.myAccountSidebar.header.aria.backArrow')}
                    />
                </div>
            )}
            {!showBackButton && (
                <Icon
                    role="button"
                    type={'close'}
                    className={style.closeIcon}
                    onClick={handleSidebarClose}
                    aria-label={translate('playtech.mobile.system.myAccountSidebar.header.aria.close')}
                />
            )}
            <div className={`${style.headerContainer} ${isBalanceButtonShown ? style.withBalance : ''}`}>
                {middle}
                {isBalanceButtonShown && (
                    <Button
                        type="button"
                        onClick={handleOpenBalancePopup}
                        className={`${style.balance}`}
                        size="medium"
                    >
                        <Suspense fallback="">
                            <Balance type="sportsbook_gaming_balance" />
                        </Suspense>
                    </Button>
                )}
            </div>
        </div>
    );
};
